import {
  StarOffIcon,
  StarOnIcon,
  AppGoogleIcon,
  AppFacebookIcon,
} from "@talladega/icons";
import classNames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { CarouselNavigation } from "@talladega/components";
import { GetBackgroundStyle, gtmPushData } from "@talladega/helpers";
import { CarouselContent, ReviewsCarouselLayout } from "@talladega/types";
import { CmsHeading } from "../CmsHeading";

export const ReviewsCarousel = ({
  heading,
  subheading,
  carouselContentList,
  background,
  baseDomain,
}: ReviewsCarouselLayout) => {
  const [emblaRef, embla] = useEmblaCarousel({
    align: "start",
    active: carouselContentList ? carouselContentList.length > 1 : false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    gtmPushData({
      event: "Review",
      element: "Slider",
      descriptor: "Previous-Customer-Review",
    });
    return embla && embla.scrollPrev();
  }, [embla]);
  const scrollNext = useCallback(() => {
    gtmPushData({
      event: "Review",
      element: "Slider",
      descriptor: "Next-Customer-Review",
    });
    return embla && embla.scrollNext();
  }, [embla]);
  const scrollTo = useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  const renderStarRating = (content: CarouselContent) => {
    return Array.from(Array(5)).map((value, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className="inline" key={`star-rating-${i}`}>
        {i !== 4 && <StarOnIcon className="inline-block text-primary-bright" />}
        {content.reviewRating === "4 Stars" && i === 4 && (
          <StarOffIcon className="inline-block text-primary-bright" />
        )}
        {content.reviewRating === "5 Stars" && i === 4 && (
          <StarOnIcon className="inline-block text-primary-bright" />
        )}
      </div>
    ));
  };

  const renderReviewSource = (content: CarouselContent) => {
    if (content.reviewSource === "Google") {
      return <AppGoogleIcon className="text-primary-bright" />;
    }
    if (content.reviewSource === "Facebook") {
      return <AppFacebookIcon className="text-primary-bright" />;
    }

    return null;
  };

  if (carouselContentList == null || carouselContentList.length === 0) {
    return null;
  }

  return (
    <div
      id="cms-reviews-carousel"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="mx-auto max-w-page items-center gap-6 px-4 pb-6 pt-10 lg:gap-24 lg:px-5 lg:py-16">
        <div>
          <div className="items-center justify-center pb-6 text-center">
            {heading && (
              <CmsHeading
                headingLevel={heading.headingGroup?.headingLevel}
                textSize={heading.headingGroup?.textSize}
                text={heading.headingGroup?.text}
                htmlId={heading.headingGroup?.htmlId}
              />
            )}
            {subheading && (
              <div className="mt-4 lg:mt-8">
                <CmsHeading
                  headingLevel={subheading.headingGroup?.headingLevel}
                  textSize={subheading.headingGroup?.textSize}
                  text={subheading.headingGroup?.text}
                  htmlId={subheading.headingGroup?.htmlId}
                />
              </div>
            )}
          </div>

          <div className="items-center justify-center text-center">
            <div
              className="max-h-[28rem] w-full overflow-hidden md:max-h-72"
              ref={emblaRef}
            >
              <div className="flex select-none">
                {carouselContentList.map((content) => (
                  <div
                    className={classNames("relative min-w-full", {
                      "cursor-pointer": carouselContentList.length > 1,
                    })}
                    key={content.contentDetail}
                  >
                    <div className="h-[28rem] overflow-hidden rounded-2xl bg-white p-6 sm:h-80 md:h-72 md:p-12 xl:h-64">
                      <h4 className="hidden leading-8 sm:block">
                        {content.contentDetail}
                      </h4>
                      <h6 className="block text-ellipsis leading-8 sm:hidden">
                        {content.contentDetail}
                      </h6>
                      <div className="my-4 inline-flex items-center justify-center">
                        <span className="border-r-2 p-2 text-lg font-normal tracking-[0.125rem] text-text-light-200">
                          {content.reviewerName}
                        </span>
                        <div className="mb-2 pl-2">
                          {renderStarRating(content)}
                        </div>
                        <div className="mx-2 mb-1">
                          {renderReviewSource(content)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="my-9 items-center justify-center text-center">
            {carouselContentList && (
              <CarouselNavigation
                buttonColours="secondary"
                contentList={carouselContentList}
                scrollNext={scrollNext}
                scrollPrev={scrollPrev}
                scrollTo={scrollTo}
                selectedIndex={selectedIndex}
                hiddenOnMobile
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default {};
